import { useDirection, useTranslation } from '@eti/providers';
import { Accordion } from '@eti/components';
import { Button, Card, Inline, Stack, Text, textVariants, vars } from '@etg/wings';
import { css } from '@eti/styles';
import { Share } from '@phosphor-icons/react';
import { useOrderDetails } from '../../containers/OrderDetailsContext';

const stepCardStyles = css`
  border: 1px solid ${vars.colors.divider};
`;

const numberBallStyles = css`
  background-color: #737373;
  border-radius: 50%;
  color: #fff;
  font-size: 0.75rem;
  height: 20px;
  line-height: 20px;
  text-align: center;
  width: 20px;
`;

const stepTextStyles = css`
  flex: 1;
`;

const dividerStyles = css`
  background-color: ${vars.colors.divider};
  height: 1px;
  width: 100%;
`;

interface RyanairFlightInfoBoxProps {
  isExpanded?: boolean;
}

const RyanairFlightInfoBox = ({ isExpanded = true }: RyanairFlightInfoBoxProps) => {
  const { t } = useTranslation();
  const { isRTL } = useDirection();
  const { ryanairLocale } = useOrderDetails();

  const ryanairVerificationSteps = [...Array(3)].map((_, index) =>
    index === 0
      ? t('OrderDetails.RyanairFlight.InfoBox.Step.1', ryanairLocale)
      : t(`OrderDetails.RyanairFlight.InfoBox.Step.${index + 1}`),
  );

  return (
    <Stack data-testid="ryanair-flight-info-box" spacing={8}>
      <Text>{t('OrderDetails.RyanairFlight.InfoBox.Description')}</Text>
      <Card className={stepCardStyles}>
        <Accordion
          id="ryanair-flight-info-accordion"
          isInitiallyExpanded={isExpanded}
          spacing={16}
          variant="full-width"
        >
          <Accordion.Header>
            <Text variant={textVariants._16_BOLD}>
              {t('OrderDetails.RyanairFlight.InfoBox.Steps.Title')}
            </Text>
          </Accordion.Header>
          <Accordion.Body>
            <Stack>
              <Stack role="list" spacing={8}>
                {ryanairVerificationSteps.map((step, index) => (
                  <Inline alignY="top" key={`step-${index}`} noWrap role="listitem">
                    <div className={numberBallStyles}>{index + 1}</div>
                    <Text asHtml={index === 0} className={stepTextStyles}>
                      {step}
                    </Text>
                  </Inline>
                ))}
              </Stack>
              <Stack>
                <Inline align="start">
                  <Button
                    aria-label={`${t('OrderDetails.RyanairFlight.InfoBox.Button.Text')}, ${t(
                      'ScreenReader.Link.OpensInNewTab.AriaLabel.Text',
                    )}`}
                    as="a"
                    data-testid="ryanair-link-button"
                    href={`https://www.ryanair.com/${ryanairLocale}`}
                    target="_blank"
                    variant="primary"
                  >
                    {t('OrderDetails.RyanairFlight.InfoBox.Button.Text')}
                    <Share aria-hidden="true" mirrored={isRTL} size={20} />
                  </Button>
                </Inline>
                <span className={dividerStyles} />
                <Text>{t('OrderDetails.RyanairFlight.InfoBox.Disclaimer')}</Text>
              </Stack>
            </Stack>
          </Accordion.Body>
        </Accordion>
      </Card>
    </Stack>
  );
};

export default RyanairFlightInfoBox;
